import type { MarketListing } from "../../domain/types.ts";
import { extractMeaningfulMarketLocality } from "../../domain/listingIdentity.ts";
import { dedupePublicMarketListings } from "../crossSourceDedupe.ts";
import {
  normalizePublicMarketListing,
  parseArea,
  parsePrice,
  publicMarketInvalidReasons,
  type PublicMarketInvalidReason
} from "../publicMarketNormalization.ts";
import type { PublicMarketQuery, RawPublicMarketListing } from "../publicMarketTypes.ts";
import { normalizeRoomCount, roomSearchVariants } from "../roomNormalization.ts";
import { canonicalCondition, normalizeSlovakLocationName } from "../../domain/propertyMatching.ts";
import { SLOVAK_MUNICIPALITIES } from "../../location/slovakMunicipalities.ts";
import {
  REALITY_RADAR_COLUMNS,
  type RealityRadarImportSnapshot,
  type RealityRadarInvalidSample,
  type RealityRadarInvalidRecord,
  type RealityRadarListingRecord,
  type RealityRadarRawRow
} from "./types.ts";
import { readFirstXlsxSheet, type XlsxCellValue } from "./xlsxReader.ts";

const REQUIRED_COLUMNS = [
  "Doména",
  "Odkaz na ponuku",
  "Typ transakcie",
  "Kategória",
  "Cena (EUR)",
  "Obec",
  "Počet izieb"
] as const;

const providerByHost: Record<string, { id: string; name: string }> = {
  "reality.bazos.sk": { id: "bazos-sk", name: "Bazoš Reality" },
  "nehnutelnosti.sk": { id: "nehnutelnosti-sk", name: "Nehnuteľnosti.sk" },
  "www.nehnutelnosti.sk": { id: "nehnutelnosti-sk", name: "Nehnuteľnosti.sk" },
  "topreality.sk": { id: "topreality-sk", name: "TopReality.sk" },
  "www.topreality.sk": { id: "topreality-sk", name: "TopReality.sk" },
  "reality.sk": { id: "reality-sk", name: "Reality.sk" },
  "www.reality.sk": { id: "reality-sk", name: "Reality.sk" },
  "reality.bazar.sk": { id: "reality-bazar-sk", name: "Bazar.sk Reality" },
  "byt.sk": { id: "byt-sk", name: "Byt.sk" },
  "www.byt.sk": { id: "byt-sk", name: "Byt.sk" },
  "areality.sk": { id: "areality-sk", name: "AReality.sk" },
  "www.areality.sk": { id: "areality-sk", name: "AReality.sk" }
};

const text = (value: unknown) => typeof value === "string" ? value.trim() : value === null || value === undefined ? "" : String(value).trim();

const verifiedMunicipalityId = (city: string, sourceLocationCode?: string) => {
  if (!sourceLocationCode) return undefined;
  const normalizedCity = normalizeSlovakLocationName(city);
  return SLOVAK_MUNICIPALITIES.find((municipality) =>
    normalizeSlovakLocationName(municipality.name) === normalizedCity
    && municipality.lau2Code === sourceLocationCode.toUpperCase()
  )?.lau2Code;
};

const meaningfulRow = (row: XlsxCellValue[]) => row.some((value) => text(value) !== "");

const safeUrl = (value: unknown) => {
  try {
    const parsed = new URL(text(value));
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.toString() : "";
  } catch {
    return "";
  }
};

const sourceForRow = (domainValue: unknown, sourceUrl: string) => {
  let host = "";
  try {
    host = new URL(safeUrl(domainValue) || sourceUrl).hostname.toLocaleLowerCase("sk");
  } catch {
    host = "";
  }
  return providerByHost[host] ?? {
    id: host ? `reality-radar:${host}` : "reality-radar:unknown",
    name: host || "Reality Radar"
  };
};

const externalIdFromUrl = (sourceUrl: string) => {
  const url = new URL(sourceUrl);
  const host = url.hostname.replace(/^www\./i, "").toLocaleLowerCase("sk");
  const segments = url.pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  const patterns = [
    /\/inzerat\/(\d+)/i,
    /\/detail\/([^/]+)/i,
    /\/reality-ponuka\/([^/?#]+)/i,
    /[?&](?:id|advertId|offerId)=([^&#]+)/i
  ];
  for (const pattern of patterns) {
    const value = sourceUrl.match(pattern)?.[1];
    if (value) return decodeURIComponent(value);
  }
  if (host === "reality.sk" && /^[A-Za-z0-9_-]{8,}$/.test(lastSegment ?? "")) {
    return decodeURIComponent(lastSegment);
  }
  if (host === "topreality.sk") return lastSegment?.match(/-(r\d+)$/i)?.[1];
  if (host === "reality.bazar.sk") return lastSegment?.match(/^(\d+)-/)?.[1];
  if (host === "byt.sk") return lastSegment?.match(/-(i\d+)$/i)?.[1];
  return undefined;
};

const titleFromUrl = (sourceUrl: string) => {
  const url = new URL(sourceUrl);
  const host = url.hostname.replace(/^www\./i, "").toLocaleLowerCase("sk");
  const segments = url.pathname.split("/").filter(Boolean);
  const raw = host === "reality.sk" ? segments[segments.length - 2] : segments[segments.length - 1];
  if (!raw) return undefined;
  const withoutId = raw
    .replace(/^\d+-/, "")
    .replace(/-(?:r|i)\d+$/i, "")
    .replace(/\.(?:html?|php)$/i, "");
  const decoded = decodeURIComponent(withoutId).replace(/[-_]+/g, " ").trim();
  return decoded || undefined;
};

const rowRecord = (headers: string[], values: XlsxCellValue[]): RealityRadarRawRow => Object.fromEntries(
  headers.map((header, index) => [header, values[index] ?? null])
) as RealityRadarRawRow;

const normalizeRealityRadarRow = (
  row: RealityRadarRawRow,
  capturedAt: string
): {
  record?: { listing: MarketListing; municipalityId?: string; transactionType?: string; rawRow: RealityRadarRawRow };
  invalidReasons: PublicMarketInvalidReason[];
} => {
  const sourceUrl = safeUrl(row["Odkaz na ponuku"]);
  const city = text(row["Obec"]);
  const propertyType = text(row["Kategória"]);
  const rawRooms = row["Počet izieb"];
  const rooms = normalizeRoomCount(rawRooms);
  const rawAskingPrice = text(row["Cena (EUR)"])
    ? row["Cena (EUR)"]
    : text(row["Mena"]).toUpperCase() === "EUR" ? row["Cena"] : undefined;
  const source = sourceForRow(row["Doména"], sourceUrl);
  const rawLocality = text(row["Lokalita"]);
  const locality = extractMeaningfulMarketLocality(rawLocality, city);
  const postalCode = text(row["PSČ"]);
  const rawArea = row["Celková plocha (m²)"];
  const area = parseArea(rawArea);
  const sourceLocationCode = text(row["Kód lokality"]) || undefined;
  const municipalityId = verifiedMunicipalityId(city, sourceLocationCode);
  const condition = text(row["Stav nehnuteľnosti"]);
  const query: PublicMarketQuery = {
    city,
    municipalityId,
    propertyType,
    rooms,
    roomVariants: roomSearchVariants(rooms),
    transactionType: text(row["Typ transakcie"]) as PublicMarketQuery["transactionType"],
    capturedAt
  };
  const raw: RawPublicMarketListing = {
    providerId: source.id,
    sourceName: source.name,
    sourceUrl,
    externalId: sourceUrl ? externalIdFromUrl(sourceUrl) : undefined,
    title: sourceUrl ? titleFromUrl(sourceUrl) : undefined,
    city,
    street: "",
    locality,
    propertyType,
    rooms: typeof rawRooms === "boolean" ? String(rawRooms) : rawRooms ?? undefined,
    area: typeof rawArea === "boolean" ? String(rawArea) : rawArea ?? undefined,
    askingPrice: typeof rawAskingPrice === "boolean" ? String(rawAskingPrice) : rawAskingPrice ?? undefined,
    pricePerM2: text(row["Cena za m² (EUR)"]) || undefined,
    condition: condition || undefined,
    capturedAt,
    status: "Aktívna ponuka",
    agency: text(row["Realitná kancelária"]) || text(row["Meno inzerenta"]) || undefined,
    municipalityId,
    location: {
      country: text(row["Štát"]) || undefined,
      region: text(row["Región"]) || undefined,
      district: text(row["Okres"]) || undefined,
      municipality: city,
      municipalityNormalized: normalizeSlovakLocationName(city),
      municipalityId,
      locality: locality || undefined,
      postalCode: postalCode || undefined,
      sourceLocationCode
    },
    areas: area ? { totalArea: area, areaBasis: "total_only" } : undefined,
    technical: { condition: canonicalCondition(condition) },
    fieldProvenance: {
      municipality: { source: "Reality Radar XLSX · Obec", confidence: municipalityId ? "high" : "medium", capturedAt, rawValue: city },
      district: { source: "Reality Radar XLSX · Okres", confidence: "high", capturedAt, rawValue: text(row["Okres"]) },
      region: { source: "Reality Radar XLSX · Región", confidence: "high", capturedAt, rawValue: text(row["Región"]) },
      locality: { source: "Reality Radar XLSX · Lokalita", confidence: locality ? "medium" : "low", capturedAt, rawValue: rawLocality },
      sourceLocationCode: { source: "Reality Radar XLSX · Kód lokality", confidence: sourceLocationCode ? "medium" : "low", capturedAt, rawValue: sourceLocationCode },
      totalArea: { source: "Reality Radar XLSX · Celková plocha (m²)", confidence: area ? "medium" : "low", capturedAt, rawValue: text(row["Celková plocha (m²)"]) },
      propertyType: { source: "Reality Radar XLSX · Kategória", confidence: "high", capturedAt, rawValue: propertyType },
      rooms: { source: "Reality Radar XLSX · Počet izieb", confidence: "high", capturedAt, rawValue: text(row["Počet izieb"]) },
      condition: { source: "Reality Radar XLSX · Stav nehnuteľnosti", confidence: condition ? "medium" : "low", capturedAt, rawValue: condition }
    }
  };
  const invalidReasons = publicMarketInvalidReasons(raw, query);
  if (invalidReasons.length > 0) return { invalidReasons };
  const listing = normalizePublicMarketListing(raw, query);
  if (!listing) return { invalidReasons: ["other"] };
  return {
    record: {
      listing,
      municipalityId,
      transactionType: text(row["Typ transakcie"]) || undefined,
      rawRow: row
    },
    invalidReasons: []
  };
};

const hashBytes = async (file: Blob) => {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
};

const createId = () => typeof crypto.randomUUID === "function"
  ? crypto.randomUUID()
  : `rr-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const normalizeRealityRadarRows = async (
  rows: XlsxCellValue[][],
  file: Blob,
  fileName: string,
  capturedAt = new Date().toISOString()
): Promise<RealityRadarImportSnapshot> => {
  const headerIndex = rows.findIndex(meaningfulRow);
  if (headerIndex < 0) throw new Error("XLSX neobsahuje žiadne dáta.");
  const headers = rows[headerIndex].map(text);
  const missing = REQUIRED_COLUMNS.filter((column) => !headers.includes(column));
  if (missing.length > 0) {
    throw new Error(`Nepodporovaný Reality Radar export. Chýbajú stĺpce: ${missing.join(", ")}.`);
  }

  const dataRows = rows.slice(headerIndex + 1).filter(meaningfulRow);
  const rowResults = dataRows.map((values, index) => {
    const rawRow = rowRecord(headers, values);
    return { ...normalizeRealityRadarRow(rawRow, capturedAt), rawRow, rowNumber: headerIndex + index + 2 };
  });
  const normalized = rowResults.flatMap((item) => item.record ? [item.record] : []);
  const invalidRecords: RealityRadarInvalidRecord[] = rowResults.flatMap((item) => item.invalidReasons.length > 0
    ? [{
      rowNumber: item.rowNumber,
      primaryReason: item.invalidReasons[0],
      reasons: item.invalidReasons,
      rawRow: item.rawRow
    }]
    : []);
  const invalidReasonCounts = invalidRecords.reduce<Partial<Record<PublicMarketInvalidReason, number>>>((counts, item) => {
    item.reasons.forEach((reason) => { counts[reason] = (counts[reason] ?? 0) + 1; });
    return counts;
  }, {});
  const invalidPrimaryReasonCounts = invalidRecords.reduce<Partial<Record<PublicMarketInvalidReason, number>>>((counts, item) => ({
    ...counts,
    [item.primaryReason]: (counts[item.primaryReason] ?? 0) + 1
  }), {});
  const invalidSample = (record: RealityRadarInvalidRecord): RealityRadarInvalidSample => ({
    rowNumber: record.rowNumber,
    municipality: text(record.rawRow["Obec"]) || undefined,
    propertyType: text(record.rawRow["Kategória"]) || undefined,
    rooms: text(record.rawRow["Počet izieb"]) || undefined,
    area: text(record.rawRow["Celková plocha (m²)"]) || undefined,
    price: text(record.rawRow["Cena (EUR)"]) || text(record.rawRow["Cena"]) || undefined,
    sourceUrl: safeUrl(record.rawRow["Odkaz na ponuku"]) || undefined
  });
  const invalidSamplesByReason = invalidRecords.reduce<RealityRadarImportSnapshot["invalidSamplesByReason"]>((samples, record) => {
    record.reasons.forEach((reason) => {
      if ((samples[reason]?.length ?? 0) >= 3) return;
      samples[reason] = [...(samples[reason] ?? []), invalidSample(record)];
    });
    return samples;
  }, {});
  const uniqueListings = dedupePublicMarketListings(normalized.map((item) => item.listing));
  const bySourceUrl = new Map<string, typeof normalized>();
  normalized.forEach((item) => {
    if (!item.listing.sourceUrl) return;
    bySourceUrl.set(item.listing.sourceUrl, [...(bySourceUrl.get(item.listing.sourceUrl) ?? []), item]);
  });
  const records: RealityRadarListingRecord[] = uniqueListings.map((listing) => {
    const sourceUrls = listing.sourceUrls?.length ? listing.sourceUrls : [listing.sourceUrl].filter(Boolean) as string[];
    const originals = sourceUrls.flatMap((url) => bySourceUrl.get(url) ?? []);
    const primary = originals[0];
    return {
      listing,
      municipalityId: primary?.municipalityId,
      transactionType: primary?.transactionType,
      rawRows: originals.map((item) => item.rawRow)
    };
  });

  return {
    id: createId(),
    connectorId: "reality-radar-xlsx-v1",
    fileName,
    fileHash: await hashBytes(file),
    capturedAt,
    columns: headers,
    rowsLoaded: dataRows.length,
    invalidRows: invalidRecords.length,
    invalidReasonCounts,
    invalidPrimaryReasonCounts,
    invalidSamplesByReason,
    invalidRecords,
    normalizedRows: normalized.length,
    duplicateRowsRemoved: normalized.length - uniqueListings.length,
    duplicatesMerged: normalized.length - uniqueListings.length,
    records
  };
};

export const parseRealityRadarXlsx = async (file: File | Blob, fileName = "reality-radar.xlsx") => {
  const rows = await readFirstXlsxSheet(file);
  return normalizeRealityRadarRows(rows, file, "name" in file && typeof file.name === "string" ? file.name : fileName);
};

export const REALITY_RADAR_REFERENCE_COLUMNS = [...REALITY_RADAR_COLUMNS];
